import { useEffect } from "react";

declare global {
  interface Window {
    googleTranslateElementInit: () => void;
    __gtWidgetInitialized?: boolean;
    google?: {
      translate?: {
        TranslateElement: new (options: object, containerId: string) => void;
        InlineLayout?: { SIMPLE: number };
      };
    };
  }
}

const ELEMENT_ID = "google_translate_element";
const SCRIPT_ID = "google-translate-script";
const STYLE_ID = "google-translate-isolation";

// ─────────────────────────────────────────────────────────────────────────────
// CSS — targets only Google-generated class names / IDs.
// Serves as the "initial paint" shield before any JS fires.
// ─────────────────────────────────────────────────────────────────────────────
const GT_CSS = `
  .goog-te-banner-frame,
  .goog-te-banner-frame.skiptranslate {
    display: none !important;
    visibility: hidden !important;
    height: 0 !important;
    max-height: 0 !important;
    overflow: hidden !important;
    top: -9999px !important;
  }

  body {
    top: 0 !important;
  }

  html, html body {
    margin-top: 0 !important;
  }

  html.translated-ltr,
  html.translated-rtl {
    margin-top: 0 !important;
  }

  html.translated-ltr body,
  html.translated-rtl body {
    top: 0 !important;
    margin-top: 0 !important;
  }

  #goog-gt-tt,
  .goog-te-balloon-frame,
  .goog-te-ftab-float,
  .goog-te-menu-frame {
    display: none !important;
  }

  .goog-text-highlight {
    background: none !important;
    background-image: none !important;
    box-shadow: none !important;
    border: none !important;
  }

  .skiptranslate.goog-te-gadget > span,
  .goog-te-gadget > span {
    display: none !important;
  }

  #${ELEMENT_ID} {
    visibility: hidden !important;
    width: 0 !important;
    height: 0 !important;
    overflow: hidden !important;
    position: absolute !important;
    pointer-events: none !important;
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Shared DOM helpers — used by observers AND the interval so logic is never
// duplicated and the two mechanisms stay in sync.
// ─────────────────────────────────────────────────────────────────────────────
const hideBannerElement = (el: HTMLElement) => {
  el.style.setProperty("display", "none", "important");
  el.style.setProperty("visibility", "hidden", "important");
  el.style.setProperty("height", "0", "important");
  el.style.setProperty("max-height", "0", "important");
  el.style.setProperty("overflow", "hidden", "important");
  el.style.setProperty("top", "-9999px", "important");
};

const resetTopOffsets = () => {
  if (!document.body || !document.documentElement) return;

  const body = document.body;
  const html = document.documentElement;

  if (body.style.top && body.style.top !== "0px") {
    body.style.setProperty("top", "0", "important");
  }
  if (body.style.marginTop && body.style.marginTop !== "0px") {
    body.style.setProperty("margin-top", "0", "important");
  }
  if (html.style.top && html.style.top !== "0px") {
    html.style.setProperty("top", "0", "important");
  }
  if (html.style.marginTop && html.style.marginTop !== "0px") {
    html.style.setProperty("margin-top", "0", "important");
  }
};

const findAndHideBanner = () => {
  const banner = document.querySelector<HTMLElement>(".goog-te-banner-frame");
  if (!banner) return;

  // Check any dimension/visibility property to decide whether action is needed.
  // Compare both "" and "0px" for height/top so browser normalisation differences
  // ("0" vs "0px") do not cause runaway calls.
  const d = banner.style.display;
  const h = banner.style.height;
  const t = banner.style.top;
  const v = banner.style.visibility;

  const isSuppressed =
    d === "none" && v === "hidden" && (h === "0" || h === "0px" || h === "") && t === "-9999px";

  if (!isSuppressed) {
    hideBannerElement(banner);
  }
};

/** Single entry-point called by every suppression mechanism. */
const suppressAll = () => {
  findAndHideBanner();
  resetTopOffsets();
};

// ─────────────────────────────────────────────────────────────────────────────
// GoogleTranslateProvider
//
// Defence layers (each independently sufficient, all stacked for reliability):
//
//  1. CSS             — hides banner before any JS fires (handles initial paint)
//  2. headObserver    — keeps our <style> LAST in <head> so our !important CSS
//                       always beats Google's own injected body{top:40px} rule
//  3. bodyObserver    — fires when Google inserts the banner iframe or changes
//                       body.style.top via JavaScript
//  4. bannerObserver  — fires on ANY attribute change to the banner iframe itself
//                       (the element Google directly mutates when showing banner)
//  5. htmlObserver    — fires when Google adds/removes translated-ltr/rtl class
//                       or touches html.style
//  6. setInterval     — the nuclear backstop; runs every 300 ms for the entire
//                       session so any banner visibility that slips through the
//                       asynchronous observer layers is caught within ≤300 ms.
//                       Overhead: one querySelector + style read = ~0.1 ms/call.
// ─────────────────────────────────────────────────────────────────────────────
const GoogleTranslateProvider = () => {
  useEffect(() => {
    const allObservers: MutationObserver[] = [];

    // ── Layer 1: CSS ───────────────────────────────────────────────────────
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement("style");
      style.id = STYLE_ID;
      style.textContent = GT_CSS;
      document.head.appendChild(style);
    }

    // ── Layer 2: head observer — keep our <style> last ─────────────────────
    // Google appends its own <style> tag after ours. Equal specificity + equal
    // !important → later in document wins. Moving ours to the end ensures we win.
    // appendChild on an existing node MOVES it — zero allocation cost.
    const headObs = new MutationObserver(() => {
      const ourStyle = document.getElementById(STYLE_ID);
      if (ourStyle && document.head.lastElementChild !== ourStyle) {
        document.head.appendChild(ourStyle);
      }
    });
    headObs.observe(document.head, { childList: true });
    allObservers.push(headObs);

    // ── Layer 3: body observer ─────────────────────────────────────────────
    // childList  → catches the banner iframe being inserted into <body>
    // attributes → catches body.style.top being set directly by Google's JS
    const bodyObs = new MutationObserver(suppressAll);
    bodyObs.observe(document.body, {
      childList: true,
      attributes: true,
      attributeFilter: ["style"],
    });
    allObservers.push(bodyObs);

    // ── Layer 4: banner-element observer (attached once banner exists) ──────
    // Google shows the banner by mutating the IFRAME element's own style
    // (e.g. banner.style.display = "block"). The body observer never sees
    // this — it watches body's attributes, not the iframe's. This dedicated
    // observer fires the instant Google touches the banner element directly.
    //
    // We watch ALL attributes (not just style) to catch any mechanism Google
    // uses: class changes, data-attributes used as show/hide signals, etc.
    let bannerObsAttached = false;

    const tryAttachBannerObs = () => {
      if (bannerObsAttached) return;
      const banner = document.querySelector<HTMLElement>(".goog-te-banner-frame");
      if (!banner) return;

      bannerObsAttached = true;
      const bannerObs = new MutationObserver(() => {
        // Guard: avoid acting when we ourselves just wrote the suppressed values,
        // which would re-trigger the observer. The isSuppressed check in
        // findAndHideBanner short-circuits without any DOM writes when values
        // are already correct.
        findAndHideBanner();
        resetTopOffsets();
      });
      bannerObs.observe(banner, { attributes: true });
      allObservers.push(bannerObs);
    };

    // ── Layer 5: html element observer ────────────────────────────────────
    // Catches html.style.top / html.style.marginTop and the
    // translated-ltr / translated-rtl class additions.
    const htmlObs = new MutationObserver(() => {
      suppressAll();
      tryAttachBannerObs(); // banner may not exist yet; retry each time html changes
    });
    htmlObs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["style", "class"],
    });
    allObservers.push(htmlObs);

    // ── Layer 6: setInterval backstop ──────────────────────────────────────
    // MutationObservers are async in the sense that Google can re-show the
    // banner inside a requestAnimationFrame, Promise microtask, or nested
    // setTimeout AFTER our observer callback has already run. The interval
    // guarantees maximum 300 ms of banner visibility under any scenario.
    const intervalId = setInterval(() => {
      suppressAll();
      tryAttachBannerObs();
    }, 300);

    // ── Initial run ────────────────────────────────────────────────────────
    suppressAll();
    tryAttachBannerObs();

    // ── Widget initialization (once per page lifetime) ─────────────────────
    if (!window.__gtWidgetInitialized) {
      window.__gtWidgetInitialized = true;

      window.googleTranslateElementInit = () => {
        if (!window.google?.translate?.TranslateElement) return;
        new window.google.translate.TranslateElement(
          { pageLanguage: "en", autoDisplay: false },
          ELEMENT_ID,
        );
      };

      if (!document.getElementById(SCRIPT_ID)) {
        const script = document.createElement("script");
        script.id = SCRIPT_ID;
        script.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
        script.async = true;
        script.onerror = () => {
          window.__gtWidgetInitialized = false;
          delete (window as any).googleTranslateElementInit;
        };
        document.head.appendChild(script);
      }
    }

    // ── Cleanup ────────────────────────────────────────────────────────────
    return () => {
      clearInterval(intervalId);
      allObservers.forEach((obs) => obs.disconnect());
    };
  }, []);

  return (
    <div
      id={ELEMENT_ID}
      aria-hidden="true"
      style={{
        visibility: "hidden",
        width: 0,
        height: 0,
        overflow: "hidden",
        position: "absolute",
        pointerEvents: "none",
      }}
    />
  );
};

export default GoogleTranslateProvider;
